import L from "leaflet";

import { Map } from "@/utils";

const coordinateConversionFlags = {
    none: 0,
    negateX: 1,
    negateY: 2,
    swapXY: 4,
};

/**
 * Parses a coordinate conversion rule bitmask into negation and swap flags.
 *
 * @param rule - The coordinate conversion rule bitmask value.
 * @returns The parsed negation and swap flags.
 */
export const parseCoordinateConversionRule = (rule) => {
    const value = parseInt(rule, 10) || 0;
    return {
        negateX: (coordinateConversionFlags.negateX & value) > 0,
        negateY: (coordinateConversionFlags.negateY & value) > 0,
        swapXY: (coordinateConversionFlags.swapXY & value) > 0,
    };
};

export const CeilingTypes = {
    tile: "Tile(Grid)",
    open: "Open",
};

const conversor = {
    toDegrees: (radians = 0) => Math.round((radians * 180) / Math.PI),
    toRadians: (degrees = 0) => (degrees * Math.PI) / 180,
};

/**
 * Represents the configuration for a floor space map.
 */
class MapSettings {
    _spaceDetails = {
        width: 10,
        height: 10,
        offSetX: 0,
        offSetY: 0,
        offSetAngle: 0,
        negateX: false,
        negateY: false,
        swapXY: false,
        spatialReferenceCode: 0,
    };
    _fixtureDetails = {
        positioningType: Map.Collections.positioningTypes[0],
        operatingPoint: 0,
        maxMountingHeight: 0,
        types: [],
    };
    _ceilingDetails = {
        ...Map.Collections.tileSizes["2x4"],
        gridOffSetX: 0,
        gridOffSetY: 0,
    };
    _geoTransform = {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 0,
        height: 0,
        width: 0,
        headingOffSet: 0,
    };

    /**
     * Creates a new MapSettings instance from API floor space data.
     *
     * @param data - The API floor space data to initialize from.
     */
    constructor(data = {}) {
        const dataPositioningTypes = [];
        if (data.hasVLC) {
            dataPositioningTypes.push("VLC");
        }
        if (data.hasBLEBeacons) {
            dataPositioningTypes.push("BLE");
        }

        if (isNaN(data.mapOffsetY)) {
            data.mapOffsetY = 0;
        }
        if (isNaN(data.mapOffsetX)) {
            data.mapOffsetX = 0;
        }
        this.spaceDetails = {
            width: Map.Converter.convertMeterToFeet(
                data.floorSpaceWidth > 0 ? data.floorSpaceWidth : 0,
                2,
            ),
            height: Map.Converter.convertMeterToFeet(
                data.floorSpaceLength > 0 ? data.floorSpaceLength : 0,
                2,
            ),
            offSetX: Map.Converter.convertMeterToFeet(data.mapOffsetX, 2),
            offSetY: Map.Converter.convertMeterToFeet(data.mapOffsetY, 2),
            offSetAngle: conversor.toDegrees(data.mapAngleOffsetRadians),
            ...parseCoordinateConversionRule(data.coordinateConversionRule),
        };
        this.fixtureDetails = {
            operatingPoint: data.operatingPoint,
            maxMountingHeight: Map.Converter.convertMeterToFeet(
                data.maxMountingHeight,
                2,
            ),
            positioningType: dataPositioningTypes.join(" + "),
            types: [{ label: data.fixtureType, value: data.fixtureType }],
        };
        this.ceilingDetails = {
            tileSizeX: data.cellWidth,
            tileSizeY: data.cellLength,
            gridOffSetX: data.gridXOffset,
            gridOffSetY: data.gridYOffset,
        };

        this.geoTransform = {
            x1: data.GTX,
            x2: data.GTXRotation,
            y1: data.GTY,
            y2: data.GTYRotation,
            height: data.GTHeight,
            width: data.GTWidth,
            headingOffSet: data.GTHeadingOffset,
        };

        if (data.floorSpaceMaps && data.floorSpaceMaps.length > 0) {
            this._floorSpaceInfo = data.floorSpaceMaps[0];
            this._floorSpaceInfo.floorSpaceName = data.floorSpaceName;
            this._floorSpaceInfo.siteName = data.siteName;
            this.spaceDetails.file = {
                name: this._floorSpaceInfo.displayName,
            };
        }
        this.dataAPI = data;
    }

    /**
     * Whether the map has valid dimensions configured.
     *
     * @returns Whether the map has valid dimensions.
     */
    get hasMap() {
        return (
            this.spaceDetails &&
            parseInt(this.spaceDetails.width, 10) > 0 &&
            parseInt(this.spaceDetails.height, 10) > 0
        );
    }

    /**
     * Sets the space detail properties, merging with existing values.
     */
    set spaceDetails(values) {
        this._spaceDetails = {
            ...this._spaceDetails,
            ...values,
        };
    }

    /**
     * Gets the space detail properties.
     *
     * @returns The space detail properties.
     */
    get spaceDetails() {
        return this._spaceDetails;
    }

    /**
     * Sets the fixture detail properties, merging with existing values.
     */
    set fixtureDetails(values) {
        this._fixtureDetails = {
            ...this._fixtureDetails,
            ...values,
        };
    }

    /**
     * Gets the fixture detail properties.
     *
     * @returns The fixture detail properties.
     */
    get fixtureDetails() {
        return this._fixtureDetails;
    }

    /**
     * Sets the ceiling detail properties, merging with existing values.
     */
    set ceilingDetails(values) {
        this._ceilingDetails = {
            ...this._ceilingDetails,
            ...values,
        };
    }

    /**
     * Gets the ceiling detail properties with resolved tile size and type.
     *
     * @returns The ceiling detail properties.
     */
    get ceilingDetails() {
        let type = this._ceilingDetails.type;
        let tileSize = {};

        for (const ceilingTypeKey in Map.Collections.tileSizes) {
            const ceilingType = Map.Collections.tileSizes[ceilingTypeKey];
            if (
                ceilingType &&
                ceilingType.tileSizeX === this._ceilingDetails.tileSizeX &&
                ceilingType.tileSizeY === this._ceilingDetails.tileSizeY
            ) {
                tileSize = ceilingType;
            }
        }
        if (!type) {
            type =
                Object.keys(tileSize).length > 0
                    ? CeilingTypes.tile
                    : CeilingTypes.open;
        }
        if (type === CeilingTypes.tile && Object.keys(tileSize).length === 0) {
            tileSize = Map.Collections.tileSizes["2x4"];
        }
        return {
            ...this._ceilingDetails,
            ...tileSize,
            type,
        };
    }

    /**
     * Sets the geo-transform properties, merging with existing values.
     */
    set geoTransform(values) {
        this._geoTransform = {
            ...this._geoTransform,
            ...values,
        };
    }

    /**
     * Gets the geo-transform properties.
     *
     * @returns The geo-transform properties.
     */
    get geoTransform() {
        return this._geoTransform;
    }

    /**
     * Gets the floor space metadata.
     *
     * @returns The floor space metadata.
     */
    get floorSpaceInfo() {
        return this._floorSpaceInfo;
    }

    /**
     * Gets the background image data with computed bounds accounting for offsets and coordinate transforms.
     *
     * @returns The background image data with bounds, or null if no floor space info exists.
     */
    get background() {
        // Converting background image data
        if (this._floorSpaceInfo) {
            const background = {};
            const { displayImageUrl } = this._floorSpaceInfo;
            background.image = this._backgroundImage
                ? this._backgroundImage
                : displayImageUrl;
            background.height = parseFloat(
                this._floorSpaceInfo.floorSpaceLength,
            );
            background.width = parseFloat(this._floorSpaceInfo.floorSpaceWidth);

            const { offSetX, offSetY } = this.spaceDetails;
            let corner1Y = background.height;
            let corner1X = 0;
            let corner2Y = 0;
            let corner2X = background.width;
            if (this.spaceDetails.negateY) {
                corner1Y += -background.height;
                corner2Y += -background.height;
            }

            if (this.spaceDetails.negateX) {
                corner1X -= background.width;
                corner2X -= background.width;
            }

            if (this.spaceDetails.swapXY) {
                corner1Y += -background.height;
                corner2Y += -background.height;

                corner1X -= Map.Converter.convertFeetToMeters(offSetY, 2);
                corner2X -= Map.Converter.convertFeetToMeters(offSetY, 2);
                corner1Y -= Map.Converter.convertFeetToMeters(offSetX, 2);
                corner2Y -= Map.Converter.convertFeetToMeters(offSetX, 2);
            } else {
                corner1X -= Map.Converter.convertFeetToMeters(offSetX, 2);
                corner2X -= Map.Converter.convertFeetToMeters(offSetX, 2);
                corner1Y -= Map.Converter.convertFeetToMeters(offSetY, 2);
                corner2Y -= Map.Converter.convertFeetToMeters(offSetY, 2);
            }
            const corner1 = L.latLng(corner1Y, corner1X);
            const corner2 = L.latLng(corner2Y, corner2X);
            background.bounds = L.latLngBounds(corner1, corner2);
            return background;
        }

        return null;
    }

    /**
     * Converts this MapSettings instance to the API request format.
     *
     * @returns The API request object.
     */
    convertToAPI() {
        const { spaceDetails, fixtureDetails, ceilingDetails, geoTransform } =
            this;
        const data = {
            ...this.dataAPI,
            floorSpaceWidth: Map.Converter.convertFeetToMeters(
                spaceDetails.width,
                2,
            ),
            floorSpaceLength: Map.Converter.convertFeetToMeters(
                spaceDetails.height,
                2,
            ),
            mapOffsetX: Map.Converter.convertFeetToMeters(
                spaceDetails.offSetX,
                2,
            ),
            mapOffsetY: Map.Converter.convertFeetToMeters(
                spaceDetails.offSetY,
                2,
            ),
            mapAngleOffsetRadians: conversor.toRadians(
                spaceDetails.offSetAngle,
            ),
            operatingPoint: fixtureDetails.operatingPoint,
            maxMountingHeight: Map.Converter.convertFeetToMeters(
                fixtureDetails.maxMountingHeight,
                2,
            ),
            fixtureType:
                fixtureDetails.types.length > 0 &&
                fixtureDetails.types[0].value,
            cellWidth: ceilingDetails.tileSizeX,
            cellLength: ceilingDetails.tileSizeY,
            gridXOffset: ceilingDetails.gridOffSetX,
            gridYOffset: ceilingDetails.gridOffSetY,
            GTX: Number(geoTransform.x1),
            GTXRotation: Number(geoTransform.x2),
            GTY: Number(geoTransform.y1),
            GTYRotation: Number(geoTransform.y2),
            GTHeight: Number(geoTransform.height),
            GTWidth: Number(geoTransform.width),
            GTHeadingOffset: Number(geoTransform.headingOffSet),
        };

        switch (fixtureDetails.positioningType) {
            case "VLC": {
                data.hasBLEBeacons = false;
                data.hasVLC = true;
                break;
            }
            case "BLE": {
                data.hasBLEBeacons = true;
                data.hasVLC = false;
                break;
            }
            default: {
                data.hasBLEBeacons = true;
                data.hasVLC = true;
            }
        }
        return data;
    }
}

export default MapSettings;
